import { useUser } from '@ad-lucy/gateway-provider';
import {
  type CampaignBaseDataCreativeInternal,
  type CampaignBaseDataCreativeInternalDeployment,
  type CampaignBaseDataCreativesInternalEdge,
  type Maybe,
  useCampaignBaseDataQuery,
} from '@codegen';
import { BookingIntegrated } from '@modules/Booking/components/BookingIntegrated';
import { LoaderCentered } from '@modules/components';
import NotFoundView from '@modules/components/NotFoundView';
import { isNonNullable } from '@modules/utils';
import { useParams } from 'react-router-dom';

export default function BookExistingCampaign() {
  const user = useUser();
  const { campaignId } = useParams<{ campaignId: string }>();

  const { data, loading: loadingCampaign } = useCampaignBaseDataQuery({
    skip: !campaignId,
    variables: { campaignId: campaignId ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const campaign = data?.campaignsBaseData?.edges?.[0]?.node;

  if (loadingCampaign) {
    return <LoaderCentered />;
  }

  if (!campaign) {
    return <NotFoundView />;
  }

  // if campaign is already booked, it should not be possible to book it again
  if (campaign.isBooked) {
    return <NotFoundView />;
  }

  const hasExternalCreativesProductFamily =
    !!campaign.productFamily?.externalCreatives;

  const creatives = (
    campaign?.creativesInternal?.edges as Array<
      Maybe<CampaignBaseDataCreativesInternalEdge>
    >
  )
    ?.map((edge: Maybe<CampaignBaseDataCreativesInternalEdge>) => edge?.node)
    .filter(isNonNullable);

  // campaign HAS NO CREATIVES -> button is disabled and tooltip is shown
  const hasActiveCreatives = Boolean(
    creatives?.some((creative: CampaignBaseDataCreativeInternal) =>
      creative.deployments?.some(
        (e: Maybe<CampaignBaseDataCreativeInternalDeployment>) =>
          e?.stage === 'PRODUCTION',
      ),
    ),
  );

  const hasExternalCreatives = (campaign.creativesExternal?.fullCount ?? 0) > 0;

  if (
    (!hasExternalCreativesProductFamily && !hasActiveCreatives) ||
    (hasExternalCreativesProductFamily && !hasExternalCreatives)
  ) {
    return <NotFoundView />;
  }

  return (
    <BookingIntegrated
      mode={
        user.require('booking.basedata.prefill:view') ? 'FRANCHISE' : 'BOOKING'
      }
      campaignId={campaignId}
    />
  );
}
